import {
    Component,
    Inject,
    OnInit,
    OnDestroy,
    TemplateRef,
    ViewChild,
    AfterViewInit,
    HostListener,
    ChangeDetectorRef
} from '@angular/core';
import {
    ApplicationFacade,
    Departure,
    DetailedRoute,
    FavoritesFacade,
    JourneyDirection,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    Stop,
    TripClickEvent
} from '@ridango/components';
import { AppStartupService, BaseComponent, DepartureArrivalTime, LatLng } from '@ridango/core';
import Env from '@ridango/core/env';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, filter, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { AddFavoriteLocationModalComponent } from './add-favorite-location-modal/add-favorite-location-modal.component';
import { ModalController } from '@ionic/angular';
import { combineLatest, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MapApiService } from '../../map/services';
import { Location } from '@angular/common';
import { Temporal } from '@js-temporal/polyfill';
import { PullToRefreshService } from '@ridango/core/lib/services';
import { IonModalService } from '@ridango/core/lib/services/ion-modal.service';
import { SmallScreenSheetModalContainerService } from '../../small-screen-sheet-modal-container/small-screen-sheet-modal-container.service';

@Component({
    selector: 'rwidget-explore-location',
    templateUrl: './explore-location.component.html',
    styleUrls: ['./explore-location.component.scss']
})
export class ExploreLocationComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('departFilterHeader') departFilterHeader: TemplateRef<any>;
    readonly NEARBY_RADIUS_METERS = 2000;
    readonly DEPARTURES_SEARCH_RANGE_SECONDS = 60 * 60 * 24;
    assetsPath$ = this.applicationFacade.assetsPath$;
    favoriteLocations$ = this.favoritesFacade.favoriteLocations$;
    departTimeFilter: DepartureArrivalTime = {
        datetime: Temporal.Now.plainDateTimeISO(this.appStartupService.getTimezone()).toString()
    };
    location;
    isFavorite: boolean;
    stopsNearLocation: Stop[] = [];
    stopIdToDepartures: Map<string, Departure[]> = new Map<string, Departure[]>();
    showNumberOfStops = 3;
    loadedStops: Stop[] = [];
    routeOptions: DetailedRoute[] = [];
    routeNameFilter: string[] = [];
    imageMapLocationPath = this.env.EXPLORE?.IMAGE_MAP_LOCATION_PATH || '/img/explore/location.svg';
    departuresLoadingMap: { [stopId: string]: boolean } = {};

    isMobileView: boolean = window.innerWidth < 768;
    initialWindowWidth: number = window.innerWidth;
    private isUpdatingRoutes = false;

    @HostListener('window:resize', ['$event'])
    onResize() {
        if (this.initialWindowWidth !== window.innerWidth) {
            this.initialWindowWidth = window.innerWidth;
            this.isMobileView = window.innerWidth < 768;
        }
    }

    constructor(
        private applicationFacade: ApplicationFacade,
        private otpService: OpenTripPlannerService,
        private favoritesFacade: FavoritesFacade,
        @Inject('ENV') public env: Env,
        private route: ActivatedRoute,
        private router: Router,
        private modalCtrl: ModalController,
        private translate: TranslateService,
        private mapApi: MapApiService,
        private routeLocation: Location,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        public appStartupService: AppStartupService,
        private pullToRefresh: PullToRefreshService,
        private ionModalService: IonModalService,
        private smallScreenSheetModalContainerService: SmallScreenSheetModalContainerService,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.favoritesFacade.getFavoriteLocations();
        this.route.queryParams
            .pipe(filter(Boolean), take(1), takeUntil(this.componentDestroyed$))
            .subscribe(({ longitude, latitude }) => {
                this.openTripPlannerFacade.fetchNearbyStops(latitude, longitude, this.NEARBY_RADIUS_METERS);
            });

        combineLatest([this.favoriteLocations$, this.route.queryParams])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([locations, params]) => {
                const longitude = params.longitude;
                const latitude = params.latitude;
                const name = params.name;
                const currentLocation = locations.find((l) => l.latitude === +latitude && l.longitude === +longitude);
                const isPOI = !!params?.pointOfInterest;
                const filterDateTime = params.filterDateTime;
                const filterType = params.filterType;

                if (
                    this.location &&
                    (this.location?.latitude !== currentLocation?.latitude ||
                        this.location?.longitude !== currentLocation?.longitude)
                ) {
                    this.departTimeFilter = {
                        datetime: Temporal.Now.plainDateTimeISO(this.appStartupService.getTimezone()).toString()
                    };
                }

                if (filterDateTime) {
                    const date = Temporal.PlainDateTime.from(filterDateTime);

                    if (
                        Temporal.Now.instant().epochSeconds <
                        date.toZonedDateTime(this.appStartupService.getTimezone()).epochSeconds
                    ) {
                        this.departTimeFilter = {
                            ...this.departTimeFilter,
                            datetime: date.toString()
                        };
                    }
                }

                if (filterType) {
                    this.departTimeFilter = {
                        ...this.departTimeFilter,
                        type: filterType as 'leaveAfter' | 'arriveBy'
                    };
                }

                if (currentLocation) {
                    this.location = currentLocation;
                    this.isFavorite = true;
                } else {
                    this.location = {
                        longitude: +longitude,
                        latitude: +latitude,
                        name
                    };
                    this.isFavorite = false;
                }

                if (isPOI) {
                    this.location = { ...this.location, pointOfInterest: true };
                }

                this.setNearbyLocationStops();

                if (params.centering) {
                    this.setMapLocationAndCenterMap();
                }
            });

        this.pullToRefresh.methodCalled$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => this.refreshStopTimes());

        if (this.env.EXPLORE?.CLEAR_ROUTE_LINE_AFTER_LOCATION_SELECTION) {
            this.openTripPlannerFacade.clearExploreMapRoute();
        }
    }

    ngAfterViewInit() {
        if (this.departFilterHeader) {
            this.smallScreenSheetModalContainerService.setHeaderTemplate(this.departFilterHeader);
        }
    }

    handleRouteFilterChange(routeShortNames) {
        this.routeNameFilter = routeShortNames;
        this.loadedStops = this.getFilteredStops().slice(0, this.showNumberOfStops);
        this.loadedStops.forEach((stop) => {
            if (!this.stopIdToDepartures.has(stop.id)) {
                this.departuresLoadingMap[stop.id] = true;
            }
        });
    }

    refreshStopTimes(): void {
        const stopIds = Array.from(this.stopIdToDepartures.keys());
        stopIds.forEach((stopId) => {
            this.departuresLoadingMap[stopId] = true;
            this.otpService
                .getUpcomingDepartures({
                    stopId,
                    timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                    numberOfDepartures: 3
                })
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((departures) => {
                    this.stopIdToDepartures.set(stopId, departures);
                })
                .add(() => {
                    this.departuresLoadingMap[stopId] = false;
                });
        });
    }

    private setMapLocationAndCenterMap() {
        this.mapApi
            .mapReady()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((ready) => {
                if (ready) {
                    const latLng: LatLng = { lat: this.location.latitude, lng: this.location.longitude };
                    this.mapApi.centerMapToCoordinates(latLng);
                }
            });
    }

    async favoriteClickHandle() {
        if (this.isFavorite) {
            this.favoritesFacade.deleteFavoriteLocation(this.location);
            this.isFavorite = false;
        } else {
            const modal = await this.modalCtrl.create({
                component: AddFavoriteLocationModalComponent,
                cssClass: 'add-favorite-location-modal',
                componentProps: {
                    location: this.location
                }
            });
            await modal.present();
        }
    }

    navigateBack() {
        this.router.navigate([this.translate.currentLang, 'explore']);
        const breakpoints = this.ionModalService.getBreakpoints();
        if (breakpoints?.length > 0) {
            this.ionModalService.setCurrentBreakpoint(breakpoints[0]);
        }
    }

    setNearbyLocationStops() {
        this.otpService
            .getStopsInRadius(this.location.latitude, this.location.longitude, this.NEARBY_RADIUS_METERS)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((response) => {
                if (!response) {
                    return;
                }
                this.stopsNearLocation = response;
                this.loadedStops = this.stopsNearLocation.slice(0, Math.max(3, this.loadedStops.length));

                this.updateStopTimesOfNewVisibleStops(this.stopsNearLocation.map((item) => item.id));
            });
    }

    private updateStopTimesOfNewVisibleStops(stopIds: string[], fromTime: number | null = null): void {
        if (this.isUpdatingRoutes) {
            return;
        }

        this.isUpdatingRoutes = true;

        stopIds.forEach((stopId) => {
            if (!this.stopIdToDepartures.has(stopId)) {
                this.departuresLoadingMap[stopId] = true;
            }
        });

        const routeMap = new Map<string, DetailedRoute>();
        this.routeOptions.forEach((route) => {
            routeMap.set(route.id, route);
        });

        const departureRequests = stopIds.map((stopId) =>
            this.otpService
                .getUpcomingDepartures({
                    stopId,
                    timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                    fromTime
                })
                .pipe(
                    distinctUntilChanged(),
                    tap((departures) => {
                        this.stopIdToDepartures.set(stopId, departures);

                        departures.forEach((departure) => {
                            if (departure.route && !routeMap.has(departure.route.id)) {
                                routeMap.set(departure.route.id, departure.route);
                            }
                        });
                    }),
                    finalize(() => {
                        this.departuresLoadingMap[stopId] = false;
                    })
                )
        );

        forkJoin(departureRequests)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: () => {
                    this.routeOptions = Array.from(routeMap.values()).sort(this.compareRouteShortNames);
                    this.isUpdatingRoutes = false;
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isUpdatingRoutes = false;
                }
            });
    }

    private compareRouteShortNames(a: DetailedRoute, b: DetailedRoute): number {
        const aNum = Number(a.shortName);
        const bNum = Number(b.shortName);

        if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
        }

        if (!isNaN(aNum)) {
            return -1;
        }

        if (!isNaN(bNum)) {
            return 1;
        }

        return a.shortName.localeCompare(b.shortName);
    }

    getStopDepartures(stopId: string) {
        const stopDepartures = this.stopIdToDepartures.get(stopId);

        if (!stopDepartures) {
            return [];
        }

        if (!this.routeNameFilter?.length) {
            return stopDepartures;
        }

        return stopDepartures.filter((d) => this.routeNameFilter.includes(d.route?.shortName));
    }

    getFilteredStops() {
        if (!this.routeNameFilter?.length) {
            return this.stopsNearLocation;
        }

        return this.stopsNearLocation.filter((stop) => this.getStopDepartures(stop.id)?.length);
    }

    openStopView(stopId: string) {
        this.router.navigate([this.translate.currentLang, 'explore', 'stop', stopId], {
            replaceUrl: this.isStopOrRouteOrLocationSelected()
        });
    }

    openTripDetailsView($event: TripClickEvent) {
        this.router
            .navigate([], {
                queryParams: {
                    ...this.route.snapshot.queryParams,
                    filterDateTime: this.departTimeFilter.datetime,
                    filterType: this.departTimeFilter.type
                },
                queryParamsHandling: 'merge',
                replaceUrl: true
            })
            .then(() => {
                this.router.navigate([this.translate.currentLang, 'explore', 'trip'], {
                    queryParams: $event,
                    replaceUrl: this.isStopOrRouteSelected()
                });
            });
    }

    showLoadMoreStops(): boolean {
        return this.getFilteredStops().length > this.showNumberOfStops;
    }

    loadMoreStops(): void {
        this.showNumberOfStops = this.showNumberOfStops + 3;
        this.loadedStops = this.getFilteredStops().slice(0, this.showNumberOfStops);
        const shouldLoadDeparturesForStops = this.getFilteredStops()
            .slice(this.showNumberOfStops - 3, this.showNumberOfStops)
            .map((stop) => stop.id);

        this.updateStopTimesOfNewVisibleStops(shouldLoadDeparturesForStops);
    }

    async scheduleClickHandle() {
        const latLng: LatLng = { lat: this.location.latitude, lng: this.location.longitude };
        this.openTripPlannerFacade.setSearchAddress(
            JourneyDirection.Destination,
            this.location.name || this.translate.instant('SELECTED_LOCATION'),
            latLng,
            false
        );

        await this.router.navigate([this.translate.currentLang, 'journey-planner', 'result']);
    }

    getData() {
        if (this.env.EXPLORE?.STOP_VIEW?.SHOW_DEPART_ARRIVE_TOGGLE) {
            this.departTimeFilter = {
                ...this.departTimeFilter,
                type: 'leaveAfter'
            };
        }

        const fromTime = Temporal.PlainDateTime.from(this.departTimeFilter.datetime).toZonedDateTime(
            this.appStartupService.getTimezone()
        ).epochSeconds;

        const stopsIds = this.stopsNearLocation.map((item) => item.id);

        this.updateStopTimesOfNewVisibleStops(stopsIds, fromTime);
    }

    private isStopOrRouteOrLocationSelected() {
        const url = this.router.createUrlTree([], { relativeTo: this.route }).toString();

        return url.includes('explore/stop') || url.includes('explore/trip') || url.includes('explore/location');
    }

    private isStopOrRouteSelected() {
        const url = this.router.createUrlTree([], { relativeTo: this.route }).toString();

        return url.includes('explore/stop') || url.includes('explore/trip');
    }

    ngOnDestroy() {
        this.smallScreenSheetModalContainerService.setHeaderTemplate(null);
        super.ngOnDestroy();
    }
}
